import React, { forwardRef } from 'react';
import NumberStepper from '@polestar/component-warehouse-react/components/number-stepper/NumberStepper';
import styled from '@emotion/styled';
import Label from '@polestar/component-warehouse-react/components/label/Label';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Caption from '@polestar/component-warehouse-react/components/caption/Caption';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useContainerFocusTriggers } from '@polestar/component-warehouse-react/hooks/useContainerFocusTriggers';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { FormFieldTooltipContainer } from '@polestar/component-warehouse-react/components/form-field/FormField';
import MinimalHitboxTypes from '@polestar/component-warehouse-react/tokens/minimal-hitbox-types/MinimalHitboxTypes';
const NumberStepperField = forwardRef(({ title, disabled = false, value = undefined, min = 0, max, hasLine, onChange, onFocus, onBlur, name, errorMessage = undefined, error = false, ariaMinusButtonLabel, ariaPlusButtonLabel, tooltip, ...props }, ref) => {
    const memoizedId = useSSRSafeId('number-stepper-field', props.id);
    const { focusBlurEvents } = useContainerFocusTriggers({
        name,
        onFocus,
        onBlur,
    });
    return (React.createElement(Container, { ...props, ...focusBlurEvents, ref: ref },
        React.createElement(FieldWrapper, null,
            title && (React.createElement(LabelWrapper, null,
                React.createElement(StyledLabel, { ...aria({ hidden: true }), id: `${memoizedId}-label`, disabled: !!disabled, tabIndex: -1 }, title),
                React.createElement(Spacer, { spacing: Spacings.xxLarge, orientation: Orientations.horizontal }))),
            React.createElement(NumberStepper, { id: memoizedId, onChange: onChange, value: value, name: name, min: min, max: max, hasLine: hasLine, disabled: disabled, errorMessage: errorMessage, error: error, ariaMinusButtonLabel: ariaMinusButtonLabel, ariaPlusButtonLabel: ariaPlusButtonLabel, inputProps: {
                    labelledby: !!title ? `${memoizedId}-label` : undefined,
                    describedby: !!errorMessage || !!error ? `${memoizedId}-message` : undefined,
                } }),
            tooltip && tooltip !== '' && (React.createElement(FormFieldTooltipWrapper, null,
                React.createElement(FormFieldTooltipContainer, { label: tooltip, orientation: Orientations.horizontal, minimalHitboxType: MinimalHitboxTypes.both })))),
        (errorMessage || (typeof error === 'string' && error !== '')) && (React.createElement(ErrorWrapper, null,
            React.createElement(Spacer, { orientation: Orientations.vertical, spacing: Spacings.xSmall }),
            React.createElement(Caption, { id: `${memoizedId}-message`, error: true, ...aria({ role: 'alert' }) }, errorMessage || error)))));
});
NumberStepperField.displayName = 'NumberStepperField';
export default NumberStepperField;
const Container = styled.div `
  display: inline-flex;
  flex-direction: column;
`;
const FieldWrapper = styled.div `
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;
const ErrorWrapper = styled.div `
  display: flex;
  margin-block-start: ${Spacings.xSmall};
`;
const LabelWrapper = styled.div `
  display: flex;
  align-self: center;
`;
const StyledLabel = styled(Label) `
  color: ${({ theme, disabled }) => theme.numberStepper.states[disabled ? 'disabled' : 'enabled'].textColor};
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const FormFieldTooltipWrapper = styled.div `
  & div {
    padding: 0.5625rem 0px 0px 0.625rem;
    padding: ${createCustomSpacing(9 / 16)} 0 0 ${createCustomSpacing(10 / 16)};
  }
  & span::before {
    inset-inline-start: 100%;
  }
`;
